import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DataManager } from '../../core/data-manager';
import { settings } from '../../config/settings';
import Select from '../../components/Select';

/**
 * Pezzi condivisi dalle sezioni di amministrazione.
 *
 * Ogni sezione e' un elenco; per creare o modificare si va su una pagina
 * dedicata (`DetailPage`), non su un modale: i form qui hanno otto o dieci
 * campi, e le sale hanno dentro tariffe e blocchi. Con una pagina vera si
 * ottengono anche indirizzo profondo e tasto Indietro del browser.
 * Al modale resta solo la conferma delle azioni distruttive.
 */

/**
 * Tetto alle pagine che si rincorrono. Non e' una scelta di prodotto: serve
 * a non trasformare un conteggio sbagliato del server in un ciclo infinito.
 */
const MAX_PAGINE = 40;

/**
 * Scarica un'entita' intera, seguendo la paginazione dell'API.
 *
 * L'API risponde a pagine e dice quante ne ha. Fermarsi alla prima, come si
 * faceva, voleva dire perdere in silenzio tutto cio' che stava oltre il
 * limite: con le prenotazioni, che si accumulano di anno in anno, lo storico
 * restava monco e i totali dell'amministratore erano semplicemente sbagliati.
 *
 * La prima pagina si aspetta - dice quante sono - le altre partono insieme:
 * in fila sarebbero una decina di viaggi buoni uno dopo l'altro.
 *
 * Su una pagina che non torna ci si ferma e si tiene quel che si ha: un
 * elenco parziale e' comunque piu' utile di una schermata di errore.
 */
export async function caricaTutti(entity, campo, verso) {
    const sorting = { field: campo || 'id', order: verso || 'desc' };
    const prima = await DataManager.getItems(entity, 1, settings.config.limit, sorting);
    if (!prima || prima.success !== 1) return prima;

    const pagine = Math.min(parseInt(prima.pages, 10) || 1, MAX_PAGINE);
    if (pagine < 2) return prima;

    const altre = await Promise.all(
        Array.from({ length: pagine - 1 }, (_, i) => (
            DataManager.getItems(entity, i + 2, settings.config.limit, sorting).catch(() => null)
        ))
    );

    let righe = prima.body || [];
    for (let i = 0; i < altre.length; i++) {
        const res = altre[i];
        if (!res || res.success !== 1) break;
        righe = righe.concat(res.body || []);
    }
    return { ...prima, body: righe };
}

/**
 * Carica tutti gli elementi di una entita'.
 *
 * `entity` nulla lascia la lista vuota senza chiamare: serve alle liste che
 * riguardano un solo ruolo, che gli altri non devono nemmeno richiedere.
 */
export function useEntityList(entity, orderField, orderDir) {
    const campo = orderField || 'id';
    const verso = orderDir || 'desc';

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(Boolean(entity));
    const [error, setError] = useState(null);

    const reload = useCallback(async () => {
        if (!entity) {
            setItems([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await caricaTutti(entity, campo, verso);
            if (res && res.success === 1) {
                setItems(res.body || []);
            }
            else {
                setError((res && res.message) || 'Impossibile caricare i dati.');
            }
        }
        catch (err) {
            setError('Impossibile contattare il server.');
        }
        finally {
            setLoading(false);
        }
    }, [entity, campo, verso]);

    useEffect(() => {
        reload();
    }, [reload]);

    return { items, loading, error, reload, setItems };
}

/** Intestazione di una sezione, con eventuale pulsante di azione. */
export function SectionHead({ eyebrow, title, subtitle, action }) {
    return (
        <div className="lv-card-head">
            <div>
                {eyebrow && <p className="lv-eyebrow">{eyebrow}</p>}
                <h2 className="lv-title lv-title-lg">{title}</h2>
                {subtitle && <p className="lv-subtitle">{subtitle}</p>}
            </div>
            {action}
        </div>
    );
}

/**
 * Rende lo stato non-nominale di una lista (caricamento, errore, vuoto).
 * Non e' un componente ma un helper: restituisce `null` quando ci sono dati
 * da mostrare, cosi' il chiamante puo' scrivere `statoLista || <tabella/>`.
 */
export function statoLista({ loading, error, empty, icon, message, onRetry }) {
    if (loading) {
        return (
            <div className="lv-empty">
                <div className="lv-empty-icon">
                    <FontAwesomeIcon icon={['fas', 'circle-notch']} spin />
                </div>
                <p className="mb-0">Caricamento in corso…</p>
            </div>
        );
    }
    if (error) {
        return (
            <div className="lv-note lv-note-error">
                <FontAwesomeIcon icon={['fas', 'triangle-exclamation']} className="me-2" />
                {error}
                {onRetry && (
                    <button type="button" className="lv-btn lv-btn-ghost lv-btn-sm ms-3" onClick={onRetry}>
                        Riprova
                    </button>
                )}
            </div>
        );
    }
    if (empty) {
        return (
            <div className="lv-empty">
                <div className="lv-empty-icon">
                    <FontAwesomeIcon icon={['fas', icon || 'inbox']} />
                </div>
                <p className="mb-0">{message || 'Nessun elemento presente.'}</p>
            </div>
        );
    }
    return null;
}

/** Campo di form: etichetta, controllo e nota esplicativa. */
export function Field({ label, hint, required, wide, children }) {
    return (
        <label className={`lv-field${wide ? ' lv-field-wide' : ''}`}>
            <span className="lv-label">
                {label}
                {required && <span className="lv-required"> *</span>}
            </span>
            {children}
            {hint && <span className="lv-hint">{hint}</span>}
        </label>
    );
}

/**
 * X di chiusura in testa al modale. Vale quanto "Annulla", ma sta dove la si
 * cerca; mentre l'operazione e' in corso e' spenta, altrimenti si potrebbe
 * chiudere il modale a salvataggio avviato.
 *
 * La usano anche i due modali fatti a mano (`lv-modal`), che non passano da
 * react-bootstrap: la X deve essere la stessa ovunque.
 */
export function ChiudiModale({ onClose, disabled }) {
    return (
        <button
            type="button"
            className="lv-modal-close"
            onClick={onClose}
            disabled={disabled}
            aria-label="Chiudi"
        >
            <FontAwesomeIcon icon={['fas', 'xmark']} />
        </button>
    );
}

/** Modale con form: il submit e' gestito dal chiamante. */
export function FormModal({ show, title, onClose, onSubmit, saving, submitLabel, size, children }) {
    return (
        <Modal show={show} onHide={saving ? undefined : onClose} centered size={size || 'lg'} backdrop="static">
            <form onSubmit={onSubmit}>
                <Modal.Header>
                    <Modal.Title className="lv-title">{title}</Modal.Title>
                    <ChiudiModale onClose={onClose} disabled={saving} />
                </Modal.Header>
                <Modal.Body>
                    <div className="lv-form-grid lv-form-grid-compact">{children}</div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="lv-btn lv-btn-ghost" onClick={onClose} disabled={saving}>
                        Annulla
                    </button>
                    <button type="submit" className="lv-btn lv-btn-primary" disabled={saving}>
                        {saving && <FontAwesomeIcon icon={['fas', 'circle-notch']} spin className="me-2" />}
                        {submitLabel || 'Salva'}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}

/** Modale di conferma per le azioni distruttive. */
export function ConfirmModal({ show, title, message, confirmLabel, danger, working, onConfirm, onClose, children }) {
    return (
        <Modal show={show} onHide={working ? undefined : onClose} centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="lv-title">{title}</Modal.Title>
                <ChiudiModale onClose={onClose} disabled={working} />
            </Modal.Header>
            <Modal.Body>
                <p className="mb-0">{message}</p>
                {children}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="lv-btn lv-btn-ghost" onClick={onClose} disabled={working}>
                    Annulla
                </button>
                <button
                    type="button"
                    className={`lv-btn ${danger ? 'lv-btn-danger' : 'lv-btn-primary'}`}
                    onClick={onConfirm}
                    disabled={working}
                >
                    {working && <FontAwesomeIcon icon={['fas', 'circle-notch']} spin className="me-2" />}
                    {confirmLabel || 'Conferma'}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

/** Pastiglia si/no per i campi booleani. */
export function BoolPill({ value, labelOn, labelOff }) {
    const attivo = parseInt(value, 10) === 1;
    return (
        <span className={`lv-pill ${attivo ? 'lv-pill-ok' : 'lv-pill-ko'}`}>
            {attivo ? (labelOn || 'Attivo') : (labelOff || 'Disattivo')}
        </span>
    );
}

/**
 * Estrae il messaggio di errore da una risposta dell'API,
 * con un fallback leggibile.
 */
export function messaggioErrore(res, fallback) {
    if (res && res.message) return res.message;
    return fallback || 'Operazione non riuscita.';
}


/** Carica un singolo record per la pagina di dettaglio. */
export function useEntityItem(entity, id) {
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(Boolean(id));
    const [error, setError] = useState(null);

    const reload = useCallback(async () => {
        if (!id) {
            setItem(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await DataManager.getItem(entity, id);
            // get_item risponde con una lista, anche per un record solo
            const trovato = res && res.success === 1 && res.body ? res.body[0] : null;
            if (trovato) setItem(trovato);
            else setError(messaggioErrore(res, 'Elemento non trovato.'));
        }
        catch (err) {
            setError('Impossibile contattare il server.');
        }
        finally {
            setLoading(false);
        }
    }, [entity, id]);

    useEffect(() => {
        reload();
    }, [reload]);

    return { item: item, loading: loading, error: error, reload: reload };
}

/**
 * Pagina di dettaglio: ritorno all'elenco, intestazione e contenuto.
 * Il form lo compone il chiamante, cosi' una sala puo' mettere sotto
 * le proprie tariffe e i propri blocchi.
 */
export function DetailPage({ backTo, backLabel, eyebrow, title, subtitle, children }) {
    return (
        <section className="lv-card lv-card-pad">
            {/* senza elenco alle spalle - l'admin di condominio ne ha uno solo -
                non c'e' nessun posto dove tornare */}
            {backTo && (
                <Link to={backTo} className="lv-back">
                    <FontAwesomeIcon icon={['fas', 'chevron-left']} />
                    {backLabel || "Torna all'elenco"}
                </Link>
            )}
            <SectionHead eyebrow={eyebrow} title={title} subtitle={subtitle} />
            {children}
        </section>
    );
}

/** Riga di azioni in fondo a una pagina di dettaglio. */
export function DetailActions({ saving, submitLabel, children }) {
    return (
        <div className="lv-detail-actions">
            <button type="submit" className="lv-btn lv-btn-primary" disabled={saving}>
                {saving && <FontAwesomeIcon icon={['fas', 'circle-notch']} spin className="me-2" />}
                {submitLabel || 'Salva'}
            </button>
            {children}
        </div>
    );
}

/* ---------------------------------------------------------- paginazione -- */

/** Righe per pagina fra cui si sceglie. La prima e' il valore di serie. */
export const PER_PAGINA = [10, 25, 50, 100];

const CHIAVE_PER_PAGINA = 'lv_per_pagina';

/* La scelta segue la persona, non l'elenco: chi lavora su uno schermo grande
   la fa una volta e la ritrova ovunque. Lo storage puo' essere negato - finestra
   anonima, cookie di terze parti bloccati - e allora si riparte dal valore di
   serie, che e' una seccatura, non un guasto. */
function perPaginaSalvato() {
    try {
        const v = parseInt(localStorage.getItem(CHIAVE_PER_PAGINA), 10);
        return PER_PAGINA.includes(v) ? v : PER_PAGINA[0];
    }
    catch (err) {
        return PER_PAGINA[0];
    }
}

/**
 * Taglia in pagine un elenco gia' filtrato.
 *
 * Filtrare, contare e sommare restano a monte, su tutte le righe: i contatori
 * delle tab e i totali dell'amministratore devono parlare dell'insieme, non
 * di cio' che si sta guardando. Qui si decide solo quanto se ne mostra.
 *
 * `chiave` e' cio' che, cambiando, riporta alla prima pagina: i filtri, o la
 * vista. Restare sulla pagina sette dopo aver ristretto a tre risultati vuol
 * dire trovare il vuoto senza capire di chi sia la colpa.
 */
export function usePaginazione(righe, chiave) {
    const [perPagina, setPerPagina] = useState(perPaginaSalvato);
    const [pagina, setPagina] = useState(1);

    useEffect(() => {
        setPagina(1);
    }, [chiave, perPagina]);

    const totale = righe.length;
    const pagine = Math.max(1, Math.ceil(totale / perPagina));
    /* L'elenco puo' accorciarsi sotto i piedi - un annullamento, un filtro
       arrivato prima del reset - e la pagina corrente non esistere piu'. */
    const corrente = Math.min(pagina, pagine);
    const da = (corrente - 1) * perPagina;

    const visibili = useMemo(() => righe.slice(da, da + perPagina), [righe, da, perPagina]);

    const cambiaPerPagina = useCallback(valore => {
        const v = parseInt(valore, 10);
        if (!PER_PAGINA.includes(v)) return;
        setPerPagina(v);
        try {
            localStorage.setItem(CHIAVE_PER_PAGINA, String(v));
        }
        catch (err) {
            /* non poterla ricordare non impedisce di usarla adesso */
        }
    }, []);

    return { visibili, pagina: corrente, setPagina, perPagina, cambiaPerPagina, totale, pagine, da };
}

/**
 * Barra di paginazione, da mettere sotto l'elenco che impagina.
 *
 * Resta al suo posto anche quando l'elenco sta in una pagina sola: il
 * conteggio dice quanto si sta guardando - che e' un'informazione pure con
 * otto righe - e la scelta di quante mostrarne deve restare a portata, non
 * comparire solo una volta superata una soglia che nessuno conosce.
 *
 * Sparisce solo a elenco vuoto, dove al suo posto parla il messaggio di
 * cortesia e "0-0 di 0" non aggiungerebbe niente.
 */
export function Pager({ stato, etichetta }) {
    const { pagina, setPagina, pagine, totale, perPagina, cambiaPerPagina, da } = stato;
    if (totale === 0) return null;

    const primo = totale === 0 ? 0 : da + 1;
    const ultimo = Math.min(da + perPagina, totale);

    return (
        <nav className="lv-pager" aria-label={etichetta || 'Paginazione'}>
            <p className="lv-pager-count">
                {primo}–{ultimo} di {totale}
            </p>
            <div className="lv-pager-nav">
                <button
                    type="button"
                    className="lv-btn lv-btn-ghost lv-btn-sm"
                    aria-label="Pagina precedente"
                    disabled={pagina <= 1}
                    onClick={() => setPagina(pagina - 1)}
                >
                    <FontAwesomeIcon icon={['fas', 'chevron-left']} />
                </button>
                <span className="lv-pager-page">Pagina {pagina} di {pagine}</span>
                <button
                    type="button"
                    className="lv-btn lv-btn-ghost lv-btn-sm"
                    aria-label="Pagina successiva"
                    disabled={pagina >= pagine}
                    onClick={() => setPagina(pagina + 1)}
                >
                    <FontAwesomeIcon icon={['fas', 'chevron-right']} />
                </button>
            </div>
            <Select
                className="lv-pager-size"
                ariaLabel="Righe per pagina"
                value={perPagina}
                onChange={cambiaPerPagina}
                options={PER_PAGINA.map(n => ({ value: n, label: `${n} per pagina` }))}
            />
        </nav>
    );
}

/**
 * Colore della pillola di stato di una prenotazione.
 *
 * Annullata e rifiutata stanno insieme in rosso: da fuori sono la stessa
 * cosa - la sala non e' piu' impegnata e quella data e' persa - e a chi
 * scorre l'elenco serve distinguerle da cio' che e' ancora in piedi, non
 * l'una dall'altra. Chi ha detto di no lo dice gia' la parola nella pillola.
 *
 * Stava in tre copie identiche, una per pagina: bastava cambiarne due su tre
 * perche' lo stesso stato avesse due colori a seconda di dove lo si guardava.
 */
export function classePillStato(stato) {
    if (stato === 'confermata') return 'lv-pill-ok';
    if (stato === 'conclusa') return 'lv-pill-neutral';
    return 'lv-pill-ko';
}

/** Bottone "Apri" della riga di un elenco: porta alla pagina dedicata. */
export function OpenLink({ to, label }) {
    return (
        <Link to={to} className="lv-btn lv-btn-ghost lv-btn-sm">
            {label || 'Apri'}
        </Link>
    );
}
